/**
 * @fileoverview Form-security strings barrel + the `formSecurity` namespace registrar.
 * @package @puffin-core/form-security
 *
 * Mirrors `@puffin-core/captcha-ui/strings`. LEAF-ONLY — the two frozen bundles, the namespace
 * constant and the resolver; no React, no component — because `@puffin-core/i18n-namespaces`
 * imports this subpath into the boot chunk of every app AND every public marketing portal, so a
 * barrel import there would put `<RateLimitNotice>` in pages that never render it.
 *
 * `loadFormSecurityStrings` is registered for every app by BOTH registries
 * (`APP_I18N_NAMESPACES` and `MARKETING_I18N_NAMESPACES`), inside `createAppI18n`, before first
 * paint. There is no self-registration from a render: that is the second delivery mechanism
 * I18N-RENDER-002 deleted from captcha-ui.
 */

import { DEFAULT_FORM_SECURITY_STRINGS } from './en.js';
import { FORM_SECURITY_STRINGS_ES } from './es.js';
import { FORM_SECURITY_NAMESPACE } from './namespace.js';

/**
 * The English bundle under a name symmetric with `FORM_SECURITY_STRINGS_ES`. Same frozen object as
 * `DEFAULT_FORM_SECURITY_STRINGS`, which keeps its own export because the fallback path imports it
 * by that name.
 */
export const FORM_SECURITY_STRINGS_EN = DEFAULT_FORM_SECURITY_STRINGS;

export { FORM_SECURITY_STRINGS_ES, DEFAULT_FORM_SECURITY_STRINGS, FORM_SECURITY_NAMESPACE };

export { resolveString } from './resolveString.js';

const BUNDLES = Object.freeze({
  en: FORM_SECURITY_STRINGS_EN,
  es: FORM_SECURITY_STRINGS_ES,
});

export const SUPPORTED_LOCALES = Object.freeze(Object.keys(BUNDLES));

/**
 * Register one or more form-security string bundles on a react-i18next instance.
 *
 * @param {object} i18nInstance - A react-i18next instance (the one the app mounts in
 *   `<I18nextProvider>`; NEVER the default singleton).
 * @param {string | string[]} [locales='en'] - Locale code(s): `'en'`, `'es'`, or any subset as an
 *   array.
 * @returns {object} The same `i18nInstance`, for chaining.
 * @throws {Error} If the instance has no `addResourceBundle()`, or a requested locale is not
 *   supported.
 */
export function loadFormSecurityStrings(i18nInstance, locales = 'en') {
  if (!i18nInstance || typeof i18nInstance.addResourceBundle !== 'function') {
    throw new Error(
      'loadFormSecurityStrings: expected a react-i18next instance with addResourceBundle()',
    );
  }
  const list = Array.isArray(locales) ? locales : [locales];
  list.forEach((locale) => {
    const bundle = BUNDLES[locale];
    if (!bundle) {
      throw new Error(
        `loadFormSecurityStrings: unsupported locale "${locale}". Supported: ${SUPPORTED_LOCALES.join(', ')}`,
      );
    }
    // deep-merge = true, overwrite = false — an app's own override always wins.
    i18nInstance.addResourceBundle(locale, FORM_SECURITY_NAMESPACE, bundle, true, false);
  });
  return i18nInstance;
}
